// IntegratedMenuManagement.jsx — 통합 메뉴 관리 페이지 (hard delete 전환)
// 운영자 + '통합 메뉴 관리' 열쇠 보유자(2026-10-15, 테넌트 관리자 자동 — 서버가 자기 테넌트 메뉴만 준다). 통합 메뉴 CRUD UI.
// 2026-10-07(사용자 결정): 표에는 작업 대상에서 고른 테넌트의 메뉴만 — 통합 관리 홈과 같은 규칙(scopedIntegratedMenus).
//   편집 창 회사 단추도 그 테넌트 회사만이라, 다른 테넌트 메뉴가 표에 섞이면 맞지 않는 단추로 고치게 된다.
import React, { useMemo, useState } from 'react';
import { useToast } from '../../components/Toast';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useIntegratedMenu } from './integratedmenu/useIntegratedMenu';
import { IntegratedMenuFormModal } from './integratedmenu/IntegratedMenuFormModal';
// 기능 이름표·회사 색은 한 곳에서 (2026-10-01) — 전엔 이 표에 계약갱신·마케터 1:1 이 빠져 영어로 보였다
import { integratedFeatureLabel, companyColor } from './integratedmenu/integratedFeatureMeta';
import { useCompanyScope } from './adminlayout/CompanyScopeContext';
import { scopedIntegratedMenus } from './adminlayout/integratedScope';
import { useCompanyNames } from './adminlayout/useCompanyNames';

export default function IntegratedMenuManagement() {
  const { showToast } = useToast();
  const hook = useIntegratedMenu({ showToast });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isMobile = useIsMobile();
  const scope = useCompanyScope();
  const tenantName = scope?.selectedTenant?.name || scope?.selectedTenant?.code || '';
  // 고른 테넌트 회사 목록이 아직 안 왔으면 아무것도 안 보인다(fail-closed — 다른 테넌트 메뉴가 섞이지 않게)
  const shownList = useMemo(() => scopedIntegratedMenus({
    isOperator: true,
    list: hook.list,
    tenantCompanyCodes: hook.companiesReady ? hook.companies.map((c) => c.code) : null,
  }), [hook.list, hook.companies, hook.companiesReady]);
  // 회사 칸 = 작업 대상과 같은 이름(2026-10-15) — 편집 창 회사 단추와 같은 목록에서
  const nameOf = useCompanyNames(hook.companies);

  function openCreate() { setEditing(null); setModalOpen(true); }
  function openEdit(menu) { setEditing(menu); setModalOpen(true); }
  function closeModal() { setModalOpen(false); setEditing(null); }

  async function handleSubmit(payload) {
    const r = editing
      ? await hook.update(editing.id, payload)
      : await hook.create(payload);
    if (r.ok) closeModal();
  }

  return (
    // 휴대폰은 공통 틀(AdminLayout main)이 좌우 16px 을 준다 — 겹치지 않게 0 (2026-09-28)
    <div style={isMobile ? { ...pageStyle, padding: '16px 0' } : pageStyle}>
      {/* ── 헤더 ────────────────────────────────────────────────────── */}
      <div style={headerCardStyle}>
        <div style={headerInnerStyle}>
          <h2 style={titleStyle}>통합 메뉴 관리</h2>
          <button onClick={openCreate} style={primaryBtnStyle}>+ 새 메뉴</button>
        </div>
        <div style={subHeaderStyle}>
          여러 회사를 통합 조회하는 관리자 메뉴를 생성합니다. 생성 후 업무/기능 배정에서 부여할 수 있습니다.
          {tenantName && <> 지금은 작업 대상 <b>{tenantName}</b>의 메뉴만 보입니다.</>}
        </div>
      </div>

      {/* ── 목록 테이블 ───────────────────────────────────────────────── */}
      <div style={tableWrapStyle}>
        {hook.loading ? (
          <div style={emptyStyle}>로딩 중...</div>
        ) : shownList.length === 0 ? (
          <div style={emptyStyle}>이 테넌트에 등록된 통합 메뉴가 없습니다. "+ 새 메뉴"로 추가하세요.</div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr style={trHeadStyle}>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>이름</th>
                <th style={thStyle}>기능</th>
                <th style={thStyle}>회사</th>
                <th style={thStyle}>정렬</th>
                <th style={thStyle}>부여</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>동작</th>
              </tr>
            </thead>
            <tbody>
              {shownList.map((m) => (
                <tr key={m.id} style={trStyle}>
                  <td style={tdStyle}>{m.id}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{m.name}</td>
                  <td style={tdStyle}>{integratedFeatureLabel(m.feature_type)}</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {(m.company_codes || []).map((c) => {
                        const col = companyColor(c);
                        return (
                          <span key={c} style={{ ...badgeStyle, background: col.bg, color: col.fg }}>{nameOf(c)}</span>
                        );
                      })}
                    </div>
                  </td>
                  <td style={tdStyle}>{m.sort_order}</td>
                  <td style={tdStyle}>
                    {m.assignees_count > 0
                      ? <span style={assigneeTagStyle}>{m.assignees_count}명</span>
                      : <span style={{ color: 'var(--text-muted, #888)' }}>0명</span>}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button onClick={() => openEdit(m)} style={smallBtnStyle}>편집</button>
                    <button onClick={() => hook.remove(m.id, m.assignees_count)} style={{ ...smallBtnStyle, color: '#dc2626', marginLeft: 4 }}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── 생성/수정 모달 ────────────────────────────────────────────── */}
      <IntegratedMenuFormModal
        open={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        editing={editing}
        companies={hook.companies}
        allowedFeatures={hook.allowedFeatures}
        menus={hook.list}
        onDelete={async (menu) => { if (await hook.remove(menu.id, menu.assignees_count)) closeModal(); }}
      />
    </div>
  );
}

// ── 스타일 ─────────────────────────────────────────────────────────────
const pageStyle = { padding: 16, maxWidth: 1100, margin: '0 auto' };
const headerCardStyle = { background: 'var(--bg-card, #fff)', borderRadius: 12, padding: 16, marginBottom: 12, border: '1px solid var(--border, #eee)' };
const headerInnerStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 };
const titleStyle = { margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text, #111)' };
const subHeaderStyle = { fontSize: 12, color: 'var(--text-muted, #888)', marginTop: 8 };
const tableWrapStyle = { background: 'var(--bg-card, #fff)', borderRadius: 12, border: '1px solid var(--border, #eee)', overflow: 'auto' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', minWidth: 720 };
const trHeadStyle = { background: 'var(--bg-subtle, #f8fafc)' };
const trStyle = { borderTop: '1px solid var(--border, #f1f5f9)' };
const thStyle = { padding: '10px 12px', fontSize: 11, fontWeight: 700, textAlign: 'left', color: 'var(--text-muted, #555)', textTransform: 'uppercase' };
const tdStyle = { padding: '10px 12px', fontSize: 13, color: 'var(--text, #111)', verticalAlign: 'middle' };
const badgeStyle = { display: 'inline-block', padding: '2px 8px', fontSize: 10, fontWeight: 700, borderRadius: 4, letterSpacing: '0.02em' };
const emptyStyle = { padding: 40, textAlign: 'center', color: 'var(--text-muted, #888)', fontSize: 13 };
const smallBtnStyle = { padding: '4px 10px', fontSize: 12, border: '1px solid var(--border, #ddd)', borderRadius: 4, background: 'var(--bg-subtle, #fff)', color: 'var(--text, #333)', cursor: 'pointer' };
const primaryBtnStyle = { padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 6, background: '#3b82f6', color: '#fff', cursor: 'pointer' };
const assigneeTagStyle = { display: 'inline-block', padding: '2px 8px', fontSize: 11, fontWeight: 700, borderRadius: 10, background: '#fef3c7', color: '#92400e' };
